import { useState, useRef, useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import { connect } from "react-redux";
import FormWizard from "../../../core/blocks/AdPlan/FormWizard";
import useWizard from "../../../core/blocks/AdPlan/useWizard";
import { CategoryField, FieldWrapper } from "../../../core/blocks/AdPlan/fields";
import FormGroupTile from "../../../core/basic/FormGroupTile";
import { Row, Col } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../../../utils/api";
import moment from "moment";
import { formatCurrency, formatPercent } from "../../../../utils/formatNumber";
import useOnScreen from "../../../../hooks/useOnScreen";
import { useMemo } from "react";
import Select from "react-select";
import { Switch } from "../../../fields/base/Switch";
import {
  STEPS,
  DEFAULT_GOALS,
  DEFAULT_BUDGET,
  DEFAULT_EXIT_CRITERIA,
  GOALS_MAP,
  BUDGET_MAP,
  EXIT_CRITERIA_MAP,
  LAUNCH_DURATION_OPTIONS,
} from "./constants";
import { Button } from "../../../core/basic/Button";
import { LAUNCH_STATES } from "./constants";
import withMediaQuery from "../../../../hocs/withMediaQuery";
import { selectModules } from "../../../../redux/selectors/modules";
import checkModule from "../../../../utils/checkModule";
import ProductDetailsDialogue from "./ProductDetailsDialogue";
import { HStack } from "../../../../../styled-system/jsx";
import { ProductSelector } from "../../../core/blocks/ProductSelector/ProductSelector";
import { DatesContext } from "../../../../dates/DatesContext";
import { HoverInfo } from "../../../core/basic/Tooltip";
import { css } from "../../../../../styled-system/css";
import { useTranslation } from "react-i18next";

const ProductLaunchForm = ({
  marketPlace: { marketPlace },
  editForm,
  launchData,
  id,
  setShowModal,
  mediaQuery,
  modules,
}) => {
  const { t } = useTranslation(["merchandising"]);
  const translatedDurationOptions = useMemo(
    () =>
      LAUNCH_DURATION_OPTIONS.map((option) => ({
        value: option.value,
        label: t(option.label, { count: option.count }),
      })),
    [t],
  );
  const translatedFormSteps = useMemo(
    () =>
      STEPS.map((step) => ({
        ...step,
        title: t(step.titleKey),
      })),
    [t],
  );
  const [formData, setFormData] = useState({
    name: editForm ? launchData?.name : "",
    products: editForm ? launchData?.products : [],
    type: "1",
    goals:
      editForm ?
        {
          margin: {
            value: launchData?.goal_metrics?.margin,
            checked: launchData?.goal_metrics?.margin ?? false,
          },
          acos: {
            value: launchData?.goal_metrics?.acos,
            checked: launchData?.goal_metrics?.acos ?? false,
          },
          minRating: {
            value: launchData?.goal_metrics?.min_rating,
            checked: launchData?.goal_metrics?.min_rating ?? false,
          },
        }
      : DEFAULT_GOALS,
    budget:
      editForm ?
        {
          advertising: {
            value: +(
              launchData?.advertising_budget / moment(launchData?.end_date).diff(moment(launchData?.start_date), "days")
            ).toFixed(2),
            checked: launchData?.advertising_budget ?? false,
          },
          promos: {
            value: +(
              launchData?.promos_budget / moment(launchData?.end_date).diff(moment(launchData?.start_date), "days")
            ).toFixed(2),
            checked: launchData?.promos_budget ?? false,
          },
        }
      : DEFAULT_BUDGET,
    exitCriteria:
      editForm ?
        {
          sales: {
            value: launchData?.exit_criteria?.sales,
            checked: launchData?.exit_criteria?.sales ?? false,
          },
          units: {
            value: launchData?.exit_criteria?.units,
            checked: launchData?.exit_criteria?.units ?? false,
          },
          numOfRatings: {
            value: launchData?.exit_criteria?.num_of_ratings,
            checked: launchData?.exit_criteria?.num_of_ratings ?? false,
          },
        }
      : DEFAULT_EXIT_CRITERIA,
    duration:
      editForm ?
        moment(launchData?.end_date).diff(moment(launchData?.start_date), "days")
      : LAUNCH_DURATION_OPTIONS[1].value,
    startDate: editForm ? new Date(launchData?.start_date) : null,
    extension: 0,
  });
  const [selectedProducts, setSelectedProducts] = useState(editForm ? launchData?.products : []);
  const [landedCostDetails, setLandedCostDetails] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(editForm ? launchData?.category : null);

  const [fieldsToValidate, setFieldsToValidate] = useState(
    editForm ? ["startDate", "duration", "goals", "exitCriteria", "budget"] : [],
  );
  const [errors, setErrors] = useState({});

  const wizard = useWizard();
  const { step } = wizard;
  const [editStep, setEditStep] = useState(null);

  const navigate = useNavigate();

  // Detect when markup in viewport, update wizard and step by user viewport without need for buttons
  const launchTypeRef = useRef();
  const productSelectionRef = useRef();
  const launchGoalsRef = useRef();
  // const reviewRef = useRef();

  const visibility = [];

  visibility[0] = useOnScreen(launchTypeRef);
  visibility[1] = useOnScreen(productSelectionRef);
  visibility[2] = useOnScreen(launchGoalsRef);

  // lastIndexOf returns -1 once the user scrolls past every tracked section (the submit area sits
  // below all three). Nothing on screen means nothing to sync, so stay on the current step rather
  // than handing FormWizard -1 as a step index.
  const visibleStep = visibility.lastIndexOf(true);
  const greatestVisibleStep = visibleStep === -1 ? step : visibleStep;

  // A step is complete once none of its fields has an error, and the next section only shows from then on
  const stepValidations = translatedFormSteps[step]?.validations;
  const readyToProceed =
    stepValidations?.length > 0 && !Object.keys(errors).some((error) => stepValidations.includes(error));
  const readyForStep = readyToProceed ? step + 1 : step;

  // A new launch's wizard follows the section on screen: back to an earlier one at any time, on to a later one once
  // the current step is complete. That's settled here, during render, whenever any of the three changes.
  const [followed, setFollowed] = useState({ visibleStep: greatestVisibleStep, step, readyToProceed });
  if (
    followed.visibleStep !== greatestVisibleStep ||
    followed.step !== step ||
    followed.readyToProceed !== readyToProceed
  ) {
    setFollowed({ visibleStep: greatestVisibleStep, step, readyToProceed });
    if (
      !editForm &&
      greatestVisibleStep !== step &&
      translatedFormSteps[greatestVisibleStep] &&
      (readyToProceed || greatestVisibleStep < step)
    ) {
      setFieldsToValidate((prevValidations) => [
        ...prevValidations,
        ...translatedFormSteps[greatestVisibleStep].validations,
      ]);
      wizard.setStep(greatestVisibleStep);
    }
  }

  const queryClient = useQueryClient();

  const onCancel = () => {
    navigate({ to: "/user/productlaunch" });
  };

  const formatLaunchData = () => {
    // SOURCE: https://stackoverflow.com/questions/54246477/how-to-convert-camelcase-to-snake-case-in-javascript
    const camelToSnakeCase = (str) => str.replaceAll(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
    const formatJSONBlob = (JSON) => {
      const formattedJSON = {};
      for (const key in JSON) {
        if (JSON[key]["checked"] !== false) {
          formattedJSON[camelToSnakeCase(key)] = JSON[key]["value"];
        }
      }

      return formattedJSON;
    };

    const formattedData = {
      name: formData.name,
      state: "INTRODUCED",
      progress: 0,
      budget: parseFloat(formData.budget.advertising.value * formData.duration),
      advertising_budget: parseFloat(formData.budget.advertising.value * formData.duration),
      exit_criteria: formatJSONBlob(formData.exitCriteria),
      start_date: formData.startDate,
      end_date: new Date(
        moment(formData.startDate)
          .add(formData.duration + formData.extension, "days")
          .format("MM-DD-YYYY"),
      ),
      goal_metrics: formatJSONBlob(formData.goals),
      products: editForm ? launchData?.products.map((p) => parseInt(p)) : formData.products.map((p) => parseInt(p)),
      category: parseInt(selectedCategory),
      marketplace: marketPlace,
    };
    return formattedData;
  };

  const createLaunch = useMutation({
    mutationFn: async (launchData) => {
      return await api.post("/merchandise/product_launch/", launchData);
    },

    onSuccess: (res) => {
      queryClient.invalidateQueries({
        queryKey: ["product_launch_summary"],
      });

      // Redirect user to newly create launch details view
      const { id } = res.data;

      navigate({
        to: "/user/productlaunch/launches/$id",
        params: { id: String(id) },
        state: { showChecklist: true },
      });
    },
  });

  const updateLaunch = useMutation({
    mutationFn: async (launchData) => {
      return await api.put(`/merchandise/product_launch/${id}/`, launchData);
    },

    onSuccess: () => {
      typeof setShowModal === "function" && setShowModal(false);
      queryClient.invalidateQueries();
    },
  });

  const onCreate = () => {
    const formattedData = formatLaunchData();

    // TODO: useMutation didn't work for loop below, revisit
    landedCostDetails.forEach((product) => {
      api.patch(`/merchandise/gvproducts/${product.id}/`, {
        landed_cost: parseFloat(product.landed_cost),
        shipping_cost: parseFloat(product.shipping_cost),
        vine_program_enrolled: product.vine_program_enrolled,
      });
    });

    createLaunch.mutate(formattedData, {
      onSuccess: () => {
        setFormData({
          goals: {},
          budget: {},
          exitCriteria: {},
        });
        setSelectedCategory(undefined);
        setSelectedProducts([]);
      },
    });
  };

  const onUpdate = () => {
    const formattedData = formatLaunchData();
    updateLaunch.mutate(formattedData);
    // Triggers updates in charts and stat layout on updated metrics
    queryClient.invalidateQueries();
  };

  const renderCogsDialogue = useMemo(() => {
    return selectedProducts?.some((p) => !p?.landed_cost || typeof p?.landed_cost === "string");
  }, [selectedProducts]);

  // Validate on data change, constantly check if ready to proceed in form workflow
  useEffect(() => {
    (() => {
      const errors = {};

      const check = (condition, key, msg) => {
        if (condition) {
          errors[key] = msg;
        }
      };

      check(
        formData.name?.length < 1 || !formData.name,
        "name",
        t(($) => $.merchandising["A launch name is required"]),
      );
      check(
        !formData?.type,
        "type",
        t(($) => $.merchandising["Launch type is required"]),
      );
      check(
        !selectedCategory,
        "category",
        t(($) => $.merchandising["Category is required"]),
      );
      check(
        (formData?.products?.length < 1 || !formData?.products) && !editForm,
        "products",
        t(($) => $.merchandising["At least 1 product is required"]),
      );
      check(
        !Object.values(formData?.goals)?.some((goal) => goal.checked && goal.value),
        "goals",
        t(($) => $.merchandising["At least 1 launch goal is required"]),
      );
      check(
        !Object.values(formData?.goals)
          ?.filter((goal) => goal.checked)
          .every((goal) => goal.value),
        "goals",
        t(($) => $.merchandising["All selected goal fields must have a value"]),
      );
      check(
        !Object.values(formData?.exitCriteria)?.some((criteria) => criteria.checked && criteria.value),
        "exitCriteria",
        t(($) => $.merchandising["At least 1 exit criteria is required"]),
      );
      check(
        !Object.values(formData?.exitCriteria)
          ?.filter((criteria) => criteria.checked)
          .every((criteria) => criteria.value),
        "exitCriteria",
        t(($) => $.merchandising["All selected exit criteria fields must have a value"]),
      );
      check(
        !Object.values(formData?.budget)?.some((budgetField) => budgetField.checked && budgetField.value),
        "budget",
        t(($) => $.merchandising["At least 1 budget field is required"]),
      );
      check(
        !Object.values(formData?.budget)
          ?.filter((budget) => budget.checked)
          .every((budget) => budget.value),
        "budget",
        t(($) => $.merchandising["All selected budget fields must have a value"]),
      );
      check(
        !formData?.startDate,
        "startDate",
        t(($) => $.merchandising["Start date is required"]),
      );
      check(
        !formData?.duration,
        "duration",
        t(($) => $.merchandising["Launch duration is required"]),
      );

      setErrors(errors);
    })();
  }, [step, formData, fieldsToValidate, selectedCategory, editForm, selectedProducts, landedCostDetails, t]);

  // Clear product selections when category changed
  useEffect(() => {
    setFormData((prevData) => ({ ...prevData, products: [] }));
    setSelectedProducts([]);
  }, [selectedCategory]);

  const renderValidationMessage = (stateName) => {
    if (errors[stateName] && fieldsToValidate?.includes(stateName)) {
      return (
        <p>
          <small className="text-danger">{errors[stateName]}</small>
        </p>
      );
    }
  };

  const generateFields = (fieldMap, formGroup, review = false) =>
    fieldMap.map((goal, i) => {
      if (review && !formData[formGroup][goal.stateName]?.checked) return null;
      const goalLabel = t(goal.label);

      return (
        <Col
          key={`${goal.stateName}-${i}`}
          xs={12}
          md={
            editForm ?
              formGroup === "budget" ?
                8
              : 4
            : 3
          }
        >
          <Row style={{ padding: editForm ? "1rem 0 0" : "2rem 0 0" }}>
            <Col xs={formGroup === "budget" ? 7 : 12}>
              <p
                style={{
                  // marginLeft: review ? "1.5rem" : "2rem",
                  fontSize: "16px",
                  display: "block",
                  fontWeight: 500,
                }}
              >
                {formGroup === "budget" ? t(($) => $.merchandising["Daily {label}"], { label: goalLabel }) : goalLabel}
              </p>
            </Col>
            <Col xs={4} className={formGroup === "budget" ? "" : "mt-4"} hidden={formGroup !== "budget" || review}>
              <Switch
                isSelected={formData[formGroup][goal.stateName]?.checked ?? goal.defaultChecked}
                onChange={() => {
                  const updatedGoals = {
                    ...formData[formGroup],
                    [goal.stateName]: {
                      ...formData[formGroup][goal.stateName],
                      checked: !formData[formGroup][goal.stateName]?.checked,
                    },
                  };
                  setFormData((prevData) => ({
                    ...prevData,
                    [formGroup]: updatedGoals,
                  }));
                }}
              />
            </Col>
          </Row>
          {(formGroup !== "budget" || formData[formGroup]?.[goal.stateName]?.["checked"]) && (
            <Row
              style={{
                display: "flex",
                alignItems: "center",
                paddingRight: "2rem",
                marginRight: "1rem",
                marginTop: editForm && formGroup === "budget" ? "-15px" : "inherit",
              }}
            >
              {!review && formGroup !== "budget" && (
                <Col xs={4} className="mt-4">
                  <Switch
                    isSelected={formData[formGroup][goal.stateName]?.checked ?? goal.defaultChecked}
                    onChange={() => {
                      const updatedGoals = {
                        ...formData[formGroup],
                        [goal.stateName]: {
                          ...formData[formGroup][goal.stateName],
                          checked: !formData[formGroup][goal.stateName]?.checked,
                        },
                      };
                      setFormData((prevData) => ({
                        ...prevData,
                        [formGroup]: updatedGoals,
                      }));
                    }}
                  />
                </Col>
              )}
              <Col
                xs={8}
                style={{
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                }}
                hidden={formGroup === "budget" && !formData[formGroup]?.[goal.stateName]?.["checked"]}
              >
                {review ?
                  <p style={{ paddingTop: "1rem" }}>
                    {goal.format === "currency" ?
                      (formatCurrency(formData[formGroup]?.[goal.stateName]?.["value"], {
                        marketPlace: marketPlace.marketPlace,
                        decimalPlaces: 0,
                      }) ?? "-")
                    : goal.format === "percent" ?
                      (formatPercent(parseFloat(formData[formGroup]?.[goal.stateName]?.["value"]) / 100, {
                        decimalPlaces: 0,
                      }) ?? "-")
                    : (formData[formGroup]?.[goal.stateName]?.["value"] ?? "-")}
                  </p>
                : <FormGroupTile
                    type="formcontrol"
                    formControlType={"number"}
                    label={undefined}
                    defaultValue={formData[formGroup][goal.stateName]?.["value"] ?? undefined}
                    prefix={
                      goal.format === "currency" ? "$"
                      : goal.format === "percentage" ?
                        "%"
                      : null
                    }
                    prefixSide={
                      goal.format === "currency" ? "left"
                      : goal.format === "percentage" ?
                        "right"
                      : null
                    }
                    handleChildFormElement={(key, value) => {
                      const updatedGoals =
                        formGroup === "budget" ?
                          {
                            ...formData[formGroup],
                            [goal.stateName]: {
                              ...formData[formGroup][goal.stateName],
                              value: value,
                            },
                          }
                        : {
                            ...formData[formGroup],
                            [goal.stateName]: {
                              ...formData[formGroup][goal.stateName],
                              value: value,
                            },
                          };
                      setFormData((prevData) => ({
                        ...prevData,
                        [formGroup]: updatedGoals,
                      }));
                    }}
                    stateName={[formGroup][goal]}
                    placeholder={t(($) => $.merchandising["Enter Goal"])}
                    style={{ marginTop: "15px" }}
                    hidden={formGroup === "budget" && !formData[formGroup]?.[goal.stateName]?.["checked"]}
                  />
                }
              </Col>
            </Row>
          )}
        </Col>
      );
    });

  const renderFormStep = () => {
    const launchTypeIndex = 0;
    const productSelectionIndex = 1;
    const launchGoalsIndex = 2;

    return (
      <div ref={launchTypeRef}>
        {readyForStep >= launchTypeIndex && (
          <div style={{ margin: mediaQuery ? "75px 35px" : "inherit" }}>
            <Row style={{ marginBottom: "75px" }}>
              <Col xs={12} md={{ span: 8, offset: 1 }} lg={{ span: 6, offset: 1 }}>
                <h2 style={{ fontWeight: 700, marginTop: 0, fontSize: "24px" }}>
                  {t(($) => $.merchandising["1. Launch Setup"])}
                </h2>
                <span
                  style={{
                    fontSize: "20px",
                    padding: "1rem 0",
                    display: "block",
                    fontWeight: 500,
                  }}
                >
                  {t(($) => $.merchandising["Name your product launch"])}
                </span>
                <FieldWrapper
                  displayError={Object.keys(errors)?.includes("name") && fieldsToValidate?.includes("name")}
                >
                  <div style={{ maxWidth: mediaQuery ? "100%" : "70%" }}>
                    <FormGroupTile
                      formControlClass="text-start"
                      type="formcontrol"
                      formControlType={"text"}
                      handleChildFormElement={(key, value) =>
                        setFormData((prevData) => ({
                          ...prevData,
                          [key]: value,
                        }))
                      }
                      defaultValue={formData.name}
                      stateName={"name"}
                      placeholder={t(($) => $.merchandising["Plan Name"])}
                      disabled={false}
                      autoFocus={true}
                      style={{ border: "1px solid #BBB" }}
                    />
                    {renderValidationMessage("name")}
                  </div>
                </FieldWrapper>
              </Col>
            </Row>
          </div>
        )}

        <div
          ref={productSelectionRef}
          id={"productSelectStep"}
          style={{
            display: readyForStep < productSelectionIndex ? "none" : "inherit",
            minHeight: "75vh",
            margin: mediaQuery ? "75px 35px" : "75px auto",
          }}
        >
          {readyForStep >= productSelectionIndex && (
            <>
              <Row>
                <Col xs={12} md={{ span: 8, offset: 1 }} lg={{ span: 6, offset: 1 }}>
                  <h2
                    style={{
                      fontWeight: 700,
                      marginTop: 0,
                      fontSize: "24px",
                    }}
                  >
                    {t(($) => $.merchandising["2. Select your products"])}
                  </h2>
                  <FieldWrapper
                    displayError={Object.keys(errors).includes("category") && fieldsToValidate?.includes("category")}
                    styles={{ marginTop: "1rem" }}
                  >
                    <div style={{ maxWidth: mediaQuery ? "100%" : "70%" }}>
                      <CategoryField
                        // The formatting below is a bit confusing, but to leverage the existing category search need to use nested marketPlace object
                        marketPlace={{ marketPlace: marketPlace }}
                        channel={"amazon"}
                        state={{ categoryId: selectedCategory }}
                        errors={{}}
                        fieldsToValidate={[]}
                        dashboard={true}
                        onChange={(key) => {
                          setSelectedCategory(key.categoryId);
                        }}
                      />
                    </div>
                    {renderValidationMessage("category")}
                  </FieldWrapper>
                </Col>
              </Row>
              <Row style={{ marginBottom: "3rem" }}>
                <Col xs={12} lg={{ span: 9, offset: 1 }}>
                  {selectedCategory && renderValidationMessage("products")}
                  <DatesContext.Provider
                    // oxlint-disable-next-line react/jsx-no-constructed-context-values
                    value={{
                      start: moment().subtract(31, "days").startOf("days"),
                      end: moment().subtract(1, "days").startOf("days"),
                      preStart: moment().subtract(31, "days").startOf("days"),
                      preEnd: moment().subtract(1, "days").startOf("days"),
                    }}
                  >
                    <ProductSelector
                      context={"newProductLaunchProductSelector"}
                      enableSelectAll={true}
                      hideClearAll={true}
                      selectedRowIds={formData?.products ?? []}
                      onSelectionChange={(selectedIds) => {
                        setFormData((prev) => ({
                          ...prev,
                          products: selectedIds,
                        }));
                      }}
                      defaultSelectedColumns={["product_title", "margin", "ad_sales", "ad_spend", "created_on"]}
                      additionalFilters={{ categoryId: selectedCategory }}
                      hideSelectedFilter={false}
                      rowIdAccessor={"product_key"}
                    />
                  </DatesContext.Provider>
                </Col>
              </Row>
              {renderCogsDialogue ?
                <Row>
                  <Col xs={12} lg={{ span: 9, offset: 1 }}>
                    <span
                      style={{
                        fontSize: "20px",
                        fontWeight: 500,
                        margin: "1rem 0",
                        padding: "1rem 1rem 1rem 0",
                      }}
                    >
                      {t(($) => $.merchandising["Landed Cost"])}
                    </span>
                    <p
                      style={{
                        fontSize: "16px",
                        marginBottom: "2rem",
                        maxWidth: mediaQuery ? "100%" : "90%",
                      }}
                    >
                      {t(
                        ($) =>
                          $.merchandising[
                            "Please provide the landed cost for the following products to track profit margins throughout the product launch."
                          ],
                      )}{" "}
                    </p>
                    <small>
                      {t(
                        ($) =>
                          $.merchandising[
                            "* If no landed and shipping costs are provided for the selected products, default values will be used to calculate margin."
                          ],
                      )}
                    </small>
                    <ProductDetailsDialogue
                      products={selectedProducts}
                      updateLandedCostDetails={setLandedCostDetails}
                      landedCostDetails={landedCostDetails}
                      marketPlace={marketPlace}
                    />
                  </Col>
                </Row>
              : null}
            </>
          )}
        </div>
        <div
          ref={launchGoalsRef}
          style={{
            display: readyForStep < launchGoalsIndex ? "none" : "inherit",
            minHeight: "70vh",
            margin: mediaQuery ? "75px 35px" : "25px auto",
          }}
        >
          {readyForStep >= launchGoalsIndex && (
            <>
              <>
                <Row>
                  <Col xs={12} md={{ span: 8, offset: 1 }}>
                    <h2
                      style={{
                        fontWeight: 700,
                        marginTop: 0,
                        fontSize: "24px",
                      }}
                    >
                      {t(($) => $.merchandising["3. Launch Goals"])}
                    </h2>
                  </Col>
                </Row>
                <Row style={{ marginTop: "1rem" }}>
                  <Col xs={12} md={{ span: 8, offset: 1 }}>
                    <span
                      style={{
                        fontSize: "20px",
                        padding: "1rem 0",
                        display: "block",
                        fontWeight: 500,
                      }}
                    >
                      {t(($) => $.merchandising["Launch Schedule"])}
                    </span>{" "}
                    <p
                      style={{
                        fontSize: "16px",
                        marginBottom: "2rem",
                        maxWidth: mediaQuery ? "100%" : "90%",
                      }}
                    >
                      {t(
                        ($) =>
                          $.merchandising[
                            "Choose the start date and duration of your new product launch. These fields can be updated later."
                          ],
                      )}
                    </p>
                    {renderValidationMessage("startDate")}
                  </Col>
                </Row>
                <Row>
                  <Col xs={{ span: 12, offset: 1 }}>
                    <Row style={{ margin: "2rem 0" }}>
                      <Col xs={12} md={3}>
                        <Row>
                          <Col xs={12}>
                            <span
                              style={{
                                fontSize: "16px",
                                padding: "1rem 0",
                                display: "block",
                                fontWeight: 500,
                              }}
                            >
                              {t(($) => $.merchandising["Start Date"])}
                            </span>
                          </Col>
                          <Col xs={12}>
                            <DatePicker
                              selected={formData?.startDate}
                              onChange={(date) =>
                                setFormData((prevFormData) => ({
                                  ...prevFormData,
                                  startDate: date,
                                }))
                              }
                              style={{ width: "100%" }}
                              onClick={(e) => e.preventDefault}
                            />
                          </Col>
                        </Row>
                      </Col>
                      <Col xs={12} md={3}>
                        <Row>
                          <Col xs={12}>
                            <span
                              style={{
                                fontSize: "16px",
                                padding: "1rem 0",
                                display: "block",
                                fontWeight: 500,
                              }}
                            >
                              {t(($) => $.merchandising["Duration"])}
                            </span>
                          </Col>
                          <Col xs={12}>
                            <Row style={{ paddingLeft: "1rem" }}>
                              <Select
                                options={translatedDurationOptions}
                                value={translatedDurationOptions.find((d) => d.value === formData.duration)}
                                onChange={(d) =>
                                  setFormData((prevData) => ({
                                    ...prevData,
                                    duration: d.value,
                                  }))
                                }
                              />
                              {renderValidationMessage("duration")}
                            </Row>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </Col>
                </Row>
                <hr className="my-5" />
                <Row>
                  <Col xs={12} md={{ span: 8, offset: 1 }}>
                    <span
                      style={{
                        fontSize: "20px",
                        padding: "1rem 0",
                        display: "block",
                        fontWeight: 500,
                      }}
                    >
                      {t(($) => $.merchandising["Budget"])}
                    </span>{" "}
                    <p
                      style={{
                        fontSize: "16px",
                        marginBottom: "2rem",
                        maxWidth: mediaQuery ? "100%" : "90%",
                      }}
                    >
                      {checkModule(modules, "MERCHANDISE_PLANS") ?
                        t(
                          ($) =>
                            $.merchandising[
                              "Select your advertising and promotions budget during the launch. These values will be used to help you quickly set up media plans and promotions."
                            ],
                        )
                      : t(
                          ($) =>
                            $.merchandising[
                              "Select your advertising budget during the launch. These values will be used to help you quickly set up media plans."
                            ],
                        )
                      }
                    </p>
                    {renderValidationMessage("budget")}
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} md={{ span: 12, offset: 1 }}>
                    <Row>{generateFields(BUDGET_MAP, "budget")}</Row>
                  </Col>
                </Row>
                <hr className="my-5" />
                <Row>
                  <Col xs={12} md={{ span: 8, offset: 1 }}>
                    <span
                      style={{
                        fontSize: "20px",
                        padding: "1rem 0",
                        display: "block",
                        fontWeight: 500,
                      }}
                    >
                      {t(($) => $.merchandising["Exit Criteria"])}
                    </span>{" "}
                    <p
                      style={{
                        fontSize: "16px",
                        marginBottom: "2rem",
                        maxWidth: mediaQuery ? "100%" : "90%",
                      }}
                    >
                      {t(
                        ($) =>
                          $.merchandising["Select which metrics you want to use to track the progress of your launch."],
                      )}
                    </p>{" "}
                    {renderValidationMessage("exitCriteria")}
                  </Col>
                </Row>
                <Row>
                  <Col xs={{ span: 12, offset: 1 }}>
                    <Row>{generateFields(EXIT_CRITERIA_MAP.slice(0, 3), "exitCriteria")}</Row>
                  </Col>
                </Row>
                <hr className="my-5" />
                <Row>
                  <Col xs={12} md={{ span: 8, offset: 1 }}>
                    <span
                      style={{
                        fontSize: "20px",
                        padding: "1rem 0",
                        display: "block",
                        fontWeight: 500,
                      }}
                    >
                      {t(($) => $.merchandising["Goals"])}
                    </span>
                    <p
                      style={{
                        fontSize: "16px",
                        marginBottom: "2rem",
                        maxWidth: mediaQuery ? "100%" : "90%",
                      }}
                    >
                      {t(
                        ($) =>
                          $.merchandising[
                            "Set launch goals that you wish to achieve throughout the product launch. These will be used to recommend a product categorization once the launch is complete."
                          ],
                      )}
                    </p>

                    {renderValidationMessage("goals")}
                  </Col>
                </Row>
                <Row className="mb-5">
                  <Col xs={12} md={{ span: 12, offset: 1 }}>
                    <Row>{generateFields(GOALS_MAP.slice(0, 3), "goals")}</Row>
                  </Col>
                </Row>
              </>

              <HStack justifyContent={"center"}>
                <Button
                  onPress={() => onCreate()}
                  isDisabled={Object.keys(errors)?.length}
                  id="submit-button"
                  isPending={createLaunch.isPending}
                >
                  {t(($) => $.merchandising["Create Launch"])}
                </Button>
              </HStack>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {editForm ?
        <>
          <div style={{ visibility: "none" }}>
            {/* <div ref={reviewRef}></div> */}
            <div ref={launchGoalsRef} />
            <div ref={launchTypeRef} />
            <div ref={productSelectionRef} />
          </div>
          <Row>
            {launchData?.state.toLowerCase() === LAUNCH_STATES.LAUNCHED.value?.toLowerCase() ?
              <Row style={{ display: "flex" }}>
                <Col xs={12} style={{ margin: "auto", textAlign: "center" }}>
                  <p>
                    {t(
                      ($) =>
                        $.merchandising[
                          "Please select a duration below to extend your launch by. After extending, you may edit your launch goals."
                        ],
                    )}
                  </p>

                  <Row
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      margin: "2rem auto",
                    }}
                  >
                    <Col xs={12} md={8}>
                      <span
                        style={{
                          fontSize: "16px",
                          padding: "1rem 0",
                          display: "block",
                          fontWeight: 500,
                          marginRight: "auto",
                          textAlign: "left",
                        }}
                      >
                        {t(($) => $.merchandising["Extend By"])}
                      </span>
                      <Select
                        options={translatedDurationOptions}
                        value={translatedDurationOptions.find((d) => d.value === formData.extension)}
                        onChange={(d) =>
                          setFormData((prevData) => ({
                            ...prevData,
                            extension: d.value,
                          }))
                        }
                      />{" "}
                    </Col>
                  </Row>
                  <HStack justifyContent={"center"}>
                    <Button onPress={() => onUpdate()} disabled={Object.keys(errors).length}>
                      {t(($) => $.merchandising["Extend Launch"])}
                    </Button>
                  </HStack>
                </Col>
              </Row>
            : <>
                <Row className="mt-4">
                  <Col xs={12} md={6} className="pe-5">
                    <Row>
                      <Col xs={12}>
                        <span
                          className={css({
                            fontSize: "20px",
                            paddingBottom: "1rem",
                            fontWeight: 500,
                            display: "flex",
                            gap: "small",
                          })}
                        >
                          {t(($) => $.merchandising["Launch Schedule"])}
                          <HoverInfo iconCss={{ color: "grey" }}>
                            {t(
                              ($) =>
                                $.merchandising[
                                  "Choose the start date and duration of your new product launch. These fields can be updated later."
                                ],
                            )}
                          </HoverInfo>
                        </span>

                        {renderValidationMessage("startDate")}
                      </Col>
                      <Col xs={6}>
                        <Row>
                          <Col xs={12}>
                            <span
                              style={{
                                fontSize: "16px",
                                padding: "1rem 0",
                                display: "block",
                                fontWeight: 500,
                              }}
                            >
                              {t(($) => $.merchandising["Start Date"])}
                            </span>
                          </Col>
                          <Col xs={12}>
                            <DatePicker
                              selected={formData?.startDate}
                              onChange={(date) =>
                                setFormData((prevFormData) => ({
                                  ...prevFormData,
                                  startDate: date,
                                }))
                              }
                              minDate={new Date(moment().format("MM-DD-YYYY"))}
                              style={{ width: "100%" }}
                              onClick={(e) => e.preventDefault}
                            />
                          </Col>
                        </Row>
                      </Col>
                      <Col xs={12} md={6}>
                        <Row>
                          <Col xs={12}>
                            <span
                              style={{
                                fontSize: "16px",
                                padding: "1rem 0",
                                display: "block",
                                fontWeight: 500,
                              }}
                            >
                              {t(($) => $.merchandising["Duration"])}
                            </span>
                          </Col>
                          <Col xs={12}>
                            <Row style={{ paddingLeft: "1rem" }}>
                              <Select
                                options={translatedDurationOptions}
                                value={translatedDurationOptions.find((d) => d.value === formData.duration)}
                                onChange={(d) =>
                                  setFormData((prevData) => ({
                                    ...prevData,
                                    duration: d.value,
                                  }))
                                }
                              />
                            </Row>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </Col>
                  <Col xs={12} md={6} className="ps-5">
                    <span
                      className={css({
                        fontSize: "20px",
                        paddingBottom: "1rem",
                        fontWeight: 500,
                        display: "flex",
                        gap: "small",
                      })}
                    >
                      {t(($) => $.merchandising["Budget"])}
                      <HoverInfo iconCss={{ color: "grey" }}>
                        {checkModule(modules, "MERCHANDISE_PLANS") ?
                          t(
                            ($) =>
                              $.merchandising[
                                "Select your advertising and promotion budget during the launch. These values will be used to help you quickly set up new media plans and promotions."
                              ],
                          )
                        : t(
                            ($) =>
                              $.merchandising[
                                "Select your advertising budget during the launch. These values will be used to help you quickly set up new media plans."
                              ],
                          )
                        }
                      </HoverInfo>
                    </span>

                    {renderValidationMessage("budget")}
                    <Row>{generateFields(BUDGET_MAP.slice(0, 3), "budget")}</Row>
                  </Col>
                </Row>
                <hr className="my-5" />
                <Col xs={12}>
                  <span
                    className={css({
                      fontSize: "20px",
                      paddingBottom: "1rem",
                      fontWeight: 500,
                      display: "flex",
                      gap: "small",
                    })}
                  >
                    {t(($) => $.merchandising["Exit Criteria"])}{" "}
                    <HoverInfo iconCss={{ color: "grey" }}>
                      {t(
                        ($) =>
                          $.merchandising[
                            "Select which metrics you want to use to track the progress of your launch. Select which metrics you want to use to track the progress of your launch."
                          ],
                      )}
                    </HoverInfo>
                  </span>
                  {renderValidationMessage("exitCriteria")}
                  <Row>{generateFields(EXIT_CRITERIA_MAP.slice(0, 3), "exitCriteria")}</Row>
                </Col>
                <hr className="my-5" />
                <Col xs={12}>
                  <span
                    className={css({
                      fontSize: "20px",
                      paddingBottom: "1rem",
                      fontWeight: 500,
                      display: "flex",
                      gap: "small",
                    })}
                  >
                    {t(($) => $.merchandising["Launch Goals"])}
                    <HoverInfo iconCss={{ color: "grey" }}>
                      {t(
                        ($) =>
                          $.merchandising[
                            "Set launch goals that you wish to achieve throughout the product launch. These will be used to recommend a product categorization once the launch is complete."
                          ],
                      )}
                    </HoverInfo>
                  </span>
                  {renderValidationMessage("goals")}
                  <Row>{generateFields(GOALS_MAP.slice(0, 3), "goals")}</Row>
                </Col>

                <HStack justifyContent={"center"} marginTop={"large"}>
                  <Button
                    onPress={() => onUpdate()}
                    isDisabled={Object.keys(errors).length}
                    isPending={updateLaunch.isPending}
                  >
                    {t(($) => $.merchandising["Update Launch"])}
                  </Button>
                </HStack>
              </>
            }
          </Row>
        </>
      : <FormWizard
          errors={errors}
          isValid={errors ? !Object.keys(errors).length : true}
          isModal={false}
          closeModal={() => {}}
          setFieldsToValidate={(fields) => setFieldsToValidate(fields)}
          wizard={wizard}
          editStep={editStep}
          setEditStep={setEditStep}
          formSteps={translatedFormSteps}
          onCreate={onCreate}
          onUpdate={onUpdate}
          clearForm={() => {}}
          onCancel={onCancel}
          showCancelButton={true}
          completeText={t(($) => $.merchandising["Activate"])}
          formToRender={<>{renderFormStep(step)}</>}
          reset={() => {
            setFormData({
              goals: {},
              budget: {},
              exitCriteria: {},
            });
            setSelectedCategory(undefined);
            setSelectedProducts([]);
          }}
          // Below for syncing latest visible step with form wizard
          autoExpandOn={true}
          autoExpandStep={greatestVisibleStep}
          formProgressRefs={[
            launchTypeRef,
            productSelectionRef,
            launchGoalsRef,
            // reviewRef,
          ]}
          onExit={() => {
            navigate({ to: "/user/productlaunch" });
          }}
          hidePreviousButtonVisibility={!step}
        />
      }
    </>
  );
};

const mapStateToProps = (state) => ({
  marketPlace: state.marketPlace,
  user: state.user,
  modules: selectModules(state),
});

export default withMediaQuery("(max-width:600px)")(connect(mapStateToProps)(ProductLaunchForm));
